import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import FulfillmentDecision from "@/components/FulfillmentDecision";
import { formatVariantLabel, sortVariants } from "@/lib/variants";
import { matchesAll } from "@/lib/trFold";
import VknLookupField from "@/components/VknLookupField";
import PageHeader from "@/components/PageHeader";
import WizardStockInfo from "@/components/WizardStockInfo";
import SearchPicker from "@/components/SearchPicker";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import NumericInput from "@/components/NumericInput";
import CurrencyChip, { CURRENCY_SYMBOL } from "@/components/CurrencyChip";
import PriceHistoryDialog, { PriceHistoryLink } from "@/components/PriceHistoryDialog";
import { useAuth } from "@/context/AuthContext";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Users, UserPlus, Package, Sparkles, Check, ChevronRight, ChevronLeft,
  Loader2, AlertTriangle, ShieldCheck, RefreshCcw, ClipboardCheck, Layers,
  Scissors, Boxes, Image as ImageIcon, Plus, X, Warehouse, Percent, Tag,
  DollarSign, Palette, Edit3, Trash2, Upload, FileText,
} from "lucide-react";
import { toast } from "sonner";

// ---- Options ---------------------------------------------------------------
const POSITION_OPTIONS = [
  { value: "gogus_sol", label: "Göğüs Sol" },
  { value: "gogus_sag", label: "Göğüs Sağ" },
  { value: "gogus_orta", label: "Göğüs Orta" },
  { value: "sirt", label: "Sırt" },
  { value: "sol_kol", label: "Sol Kol" },
  { value: "sag_kol", label: "Sağ Kol" },
  { value: "yaka_arka", label: "Yaka Arkası" },
  { value: "sapka_on", label: "Şapka Ön" },
  { value: "sapka_yan", label: "Şapka Yan" },
  { value: "sapka_arka", label: "Şapka Arka" },
  { value: "on_etek", label: "Ön Etek" },
  { value: "arka_etek", label: "Arka Etek" },
];
const TECHNIQUE_OPTIONS = [
  { value: "baski", label: "Baskı (Serigrafi)" },
  { value: "nakis", label: "Nakış" },
  { value: "transfer", label: "Transfer" },
  { value: "arma", label: "Arma" },
  { value: "dtf", label: "DTF" },
  { value: "sublime", label: "Sublimasyon" },
  { value: "flock", label: "Flok" },
];

const STEPS = [
  { key: "customer", label: "Müşteri", icon: Users },
  { key: "lines", label: "Sipariş Satırları", icon: Package },
  { key: "bom", label: "Malzeme (Ürün Reçetesi)", icon: Layers },
  { key: "review", label: "Özet & AI", icon: Sparkles },
];

const CURRENCIES = ["TRY", "USD", "EUR", "GBP"];

const emptyDraft = () => ({
  product_id: "",
  variant_id: "",
  warehouse_id: "",
  quantity: 0,
  unit_price: 0,
  base_price: 0,
  // FIX (manuel fiyat regresyonu): kullanıcı fiyat alanına elle değer girdiğinde
  // bu flag true olur. onProductSelected'ın async fallback (BOM / sale_price /
  // base_price) patch'leri bu flag true iken unit_price'ı ASLA ezmez.
  unit_price_manual: false,
  currency: "TRY",
  tax_rate: 10,  // FAZ 5 M5: default 10% (matches product default)
  discount_percent: 0,
  discount_amount: 0,
  notes: "",
  logos: [],
});

// ---- Money helpers ---------------------------------------------------------
const fmt2 = (n) => Number(n || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const currencySymbol = (c) => CURRENCY_SYMBOL[(c || "TRY").toUpperCase()] || "₺";

// Baskı/Nakış uygulaması: varsayılan tüm varyantlar; kapsam kapalıysa yalnız seçilenler
const appIncludes = (a, vid) => a.apply_all !== false || (a.variant_ids || []).includes(vid || "");
const appQty = (a, l) => {
  const sq = Object.entries(l.size_quantities || {}).filter(([, q]) => Number(q) > 0);
  if (sq.length) return sq.filter(([vid]) => appIncludes(a, vid)).reduce((s, [, q]) => s + Number(q), 0);
  return appIncludes(a, l.variant_id) ? Number(l.quantity) || 0 : 0;
};
const APP_TYPES = [["baski", "Baskı"], ["nakis", "Nakış"]];

const computeLine = (l) => {
  const qty = Number(l.quantity) || 0;
  const gross = qty * (Number(l.unit_price) || 0);
  const disc = Number(l.discount_amount || 0) + gross * (Number(l.discount_percent || 0) / 100);
  const appsSub = (l.apps || []).reduce((s, a) => s + appQty(a, l) * (Number(a.unit_price) || 0), 0);
  const subtotal = Math.max(0, gross - disc) + appsSub;
  const tax = subtotal * (Number(l.tax_rate || 0) / 100);
  return { qty, gross, discount: disc, subtotal, tax, total: subtotal + tax };
};

// Convert amount from currency A to currency B via TCMB TRY rates
const convertPrice = (amount, from, to, rates) => {
  if (!rates || from === to) return amount;
  const rFrom = Number(rates[from] || 1);
  const rTo = Number(rates[to] || 1);
  if (!rFrom || !rTo) return amount;
  // amount is in `from`. Convert to TRY first, then to `to`.
  const inTry = Number(amount) * rFrom;
  return inTry / rTo;
};

// ---- Main ------------------------------------------------------------------
export default function OrderWizard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const lockedMode = ["quote", "order"].includes(searchParams.get("mode")) ? searchParams.get("mode") : null;
  const initialMode = searchParams.get("mode") === "quote" ? "quote" : "order";
  const [createAs, setCreateAs] = useState(initialMode); // "order" | "quote"
  const { can: canPerm } = useAuth();
  // SEC-003: backend /orders/wizard ile birebir (wizard.add veya orders/quotes.add)
  const canCreateAs = (m) => canPerm("wizard", "add") || canPerm(m === "quote" ? "quotes" : "orders", "add") || (m === "quote" && canPerm("orders", "add"));
  const [stepIdx, setStepIdx] = useState(0);

  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [variantsByProduct, setVariantsByProduct] = useState({});
  const [artworkByCustomer, setArtworkByCustomer] = useState({});
  const [artworkPreviews, setArtworkPreviews] = useState({});
  const [fxRates, setFxRates] = useState(null);

  const [customerId, setCustomerId] = useState("");
  const [savedLines, setSavedLines] = useState([]);
  const [draft, setDraft] = useState(emptyDraft());
  const [editingIdx, setEditingIdx] = useState(-1); // -1 = new draft
  const [dueDate, setDueDate] = useState("");
  const [deliveryTerm, setDeliveryTerm] = useState("");
  const [paymentTerms, setPaymentTerms] = useState("");
  const [priority, setPriority] = useState("normal");
  const [notes, setNotes] = useState("");

  const [aiReview, setAiReview] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [bomCheck, setBomCheck] = useState(null);
  const [bomLoading, setBomLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // Risk uyarısı (P2)
  const [riskInfo, setRiskInfo] = useState(null);
  const [riskDialogOpen, setRiskDialogOpen] = useState(false);
  const [riskChecking, setRiskChecking] = useState(false);
  // P3: Numune → Sipariş bağlantısı
  // sessionStorage'dan gelen intent — sample_id, lines[{item_id, product_id, variant_id, quantity, unit_price}]
  const [sampleConvertIntent, setSampleConvertIntent] = useState(null);

  // ---- Bootstrap -----------------------------------------------------------
  useEffect(() => {
    (async () => {
      try {
        const [c, p, w, fx] = await Promise.all([
          api.get("/customers"),
          api.get("/products"),
          api.get("/warehouses"),
          api.get("/finance/exchange-rates").catch(() => ({ data: null })),
        ]);
        setCustomers(c.data || []);
        setProducts(p.data || []);
        const activeWhs = (w.data || []).filter((x) => x.is_active !== false);
        setWarehouses(activeWhs);
        // FAZ A: tek depo varsa yeni draft için otomatik seç
        if (activeWhs.length === 1) {
          setDraft((prev) => (prev.warehouse_id ? prev : { ...prev, warehouse_id: activeWhs[0].id }));
        }
        setFxRates(fx.data);
        // URL'de ?customer=<id> parametresi varsa müşteriyi otomatik seç
        // (Müşteri sayfasından "Sipariş / Teklif Hazırla" tıklandığında geliyor)
        const pre = searchParams.get("customer");
        if (pre && (c.data || []).some((x) => x.id === pre)) {
          setCustomerId(pre);
        }
        // P3: Numune → Sipariş bağlantısı
        const fromSample = searchParams.get("from_sample");
        if (fromSample) {
          try {
            const raw = sessionStorage.getItem("operix.sample_convert");
            if (raw) {
              const intent = JSON.parse(raw);
              if (intent?.sample_id === fromSample && Array.isArray(intent.lines)) {
                setSampleConvertIntent(intent);
                if (intent.customer_id && (c.data || []).some((x) => x.id === intent.customer_id)) {
                  setCustomerId(intent.customer_id);
                }
                // Prefill savedLines — sample_out zaten stok düşürdü, wizard'a from_sample=true flag
                const preLines = intent.lines
                  .filter((l) => l.product_id)  // Sadece ürün eşleşmiş satırlar otomatik prefill
                  .map((l) => ({
                    _lid: Math.random().toString(36).slice(2),
                    product_id: l.product_id,
                    variant_id: l.variant_id || "",
                    warehouse_id: activeWhs[0]?.id || "",
                    quantity: Number(l.quantity) || 1,
                    unit_price: Number(l.unit_price) || 0,
                    base_price: Number(l.unit_price) || 0,
                    unit_price_manual: (Number(l.unit_price) || 0) > 0,
                    currency: "TRY",
                    tax_rate: 10,
                    discount_percent: 0,
                    discount_amount: 0,
                    notes: `Numune dönüşümü · sample_item=${l.item_id}`,
                    logos: [],
                    from_sample_item_id: l.item_id,   // Post-commit callback için
                  }));
                if (preLines.length > 0) {
                  setSavedLines(preLines);
                  toast.info(`Numuneden ${preLines.length} kalem prefill edildi. Stok tekrar düşmez — bu satırlar sample_out ile zaten çıktı.`);
                }
                const stockLess = intent.lines.filter((l) => !l.product_id);
                if (stockLess.length > 0) {
                  toast.warning(`${stockLess.length} kalem Stok Dışı Numune — ürünü manuel seçin.`);
                }
              }
            }
          } catch { /* ignore */ }
        }
      } catch (err) { toast.error(formatApiError(err)); }
    })();
  }, []); // eslint-disable-line

  // ---- Variants for draft product ------------------------------------------
  useEffect(() => {
    const pid = draft.product_id;
    if (!pid || variantsByProduct[pid] !== undefined) return;
    (async () => {
      try {
        const { data } = await api.get(`/products/${pid}/variants`);
        setVariantsByProduct((prev) => ({ ...prev, [pid]: data || [] }));
      } catch { setVariantsByProduct((prev) => ({ ...prev, [pid]: [] })); }
    })();
  }, [draft.product_id]); // eslint-disable-line

  // Also warm-load variants for saved lines (for display)
  useEffect(() => {
    savedLines.forEach(async (l) => {
      if (!l.product_id || variantsByProduct[l.product_id] !== undefined) return;
      try {
        const { data } = await api.get(`/products/${l.product_id}/variants`);
        setVariantsByProduct((prev) => ({ ...prev, [l.product_id]: data || [] }));
      } catch { /* ignore */ }
    });
  }, [savedLines]); // eslint-disable-line

  // ---- Customer artwork library --------------------------------------------
  const reloadArtwork = useCallback(async () => {
    if (!customerId) return;
    try {
      const { data } = await api.get(`/artwork/customer/${customerId}`);
      setArtworkByCustomer((prev) => ({ ...prev, [customerId]: data || [] }));
      for (const a of data || []) {
        if (!a.content_type?.startsWith("image/") || artworkPreviews[a.id]) continue;
        try {
          const r = await api.get(`/artwork/download/${a.id}`, { responseType: "blob" });
          setArtworkPreviews((prev) => ({ ...prev, [a.id]: URL.createObjectURL(r.data) }));
        } catch { /* ignore */ }
      }
    } catch { setArtworkByCustomer((prev) => ({ ...prev, [customerId]: [] })); }
  }, [customerId, artworkPreviews]);
  useEffect(() => {
    if (!customerId || artworkByCustomer[customerId]) return;
    reloadArtwork();
  }, [customerId]); // eslint-disable-line

  // ---- Müşteri değişince önceki logo bağlantılarını temizle (SEC/UX) -------
  // Kaydedilmiş satırlarda ya da draft'ta başka müşteriye ait logo referansları
  // (artwork_id) varsa temizlenir. Aksi halde yanlış müşteriye baskı çıkabilir.
  const prevCustomerIdRef = useRef(customerId);
  useEffect(() => {
    const prev = prevCustomerIdRef.current;
    prevCustomerIdRef.current = customerId;
    if (!prev || prev === customerId) return;  // ilk atama veya değişiklik yok
    let cleared = 0;
    setSavedLines((lines) =>
      lines.map((l) => {
        const logos = l.logos || [];
        if (logos.length === 0) return l;
        cleared += logos.length;
        return { ...l, logos: [] };
      }),
    );
    setDraft((d) => {
      const logos = d.logos || [];
      if (logos.length === 0) return d;
      cleared += logos.length;
      return { ...d, logos: [] };
    });
    const clearApps = (x) => {
      const n = (x.apps || []).reduce((s, a) => s + (a.logos || []).length, 0);
      if (!n) return x;
      cleared += n;
      return { ...x, apps: x.apps.map((a) => ({ ...a, logos: [] })) };
    };
    setSavedLines((lines) => lines.map(clearApps));
    setDraft(clearApps);
    if (cleared > 0) {
      toast.warning(
        `Müşteri değişti. ${cleared} logo bağlantısı temizlendi — Logo seçimini yeniden kontrol edin.`,
      );
    }
  }, [customerId]);

  // ---- Totals --------------------------------------------------------------
  const totalQty = useMemo(
    () => savedLines.reduce((s, l) => s + (Number(l.quantity) || 0), 0),
    [savedLines],
  );
  const grandTotals = useMemo(() => {
    // FAZ 5 M6 — Multi-currency totals: teklif aşamasında para birimleri
    // BİRBİRİNE ÇEVRİLMEZ. Her currency kendi grubunda toplanır.
    // TCMB kuru sadece:
    //   (a) risk-check için TRY-equivalent hesabında (internal)
    //   (b) UI'da alt bilgi olarak "TCMB … " satırında
    // kullanılır. Toplam kutularında ASLA çevrilmez.
    const perCurrency = {};  // { TRY: {subtotal, discount, tax, total}, ... }
    let totalTRY = 0;
    for (const l of savedLines) {
      const c = computeLine(l);
      const cur = (l.currency || "TRY").toUpperCase();
      if (!perCurrency[cur]) {
        perCurrency[cur] = { subtotal: 0, discount: 0, tax: 0, total: 0 };
      }
      perCurrency[cur].subtotal += c.subtotal;
      perCurrency[cur].discount += c.discount;
      perCurrency[cur].tax += c.tax;
      perCurrency[cur].total += c.total;
      // TRY-equivalent (yalnız risk-check için, UI'da gösterilmez)
      totalTRY += convertPrice(c.total, cur, "TRY", fxRates) || 0;
    }
    const order = { TRY: 0, USD: 1, EUR: 2, GBP: 3 };
    const currencies = Object.keys(perCurrency).sort(
      (a, b) => (order[a] ?? 99) - (order[b] ?? 99) || a.localeCompare(b),
    );
    // Backward-compat: `total` = TRY-equivalent (risk-check kullanır)
    return {
      perCurrency,
      currencies,
      totalTRY,
      // Legacy aliases (deprecated — sadece risk-check gibi tek TRY tutar
      // isteyen tüketiciler için). Toplam kutularında kullanılmaz.
      subtotal: currencies.reduce((s, c) => s + (convertPrice(perCurrency[c].subtotal, c, "TRY", fxRates) || 0), 0),
      discount: currencies.reduce((s, c) => s + (convertPrice(perCurrency[c].discount, c, "TRY", fxRates) || 0), 0),
      tax: currencies.reduce((s, c) => s + (convertPrice(perCurrency[c].tax, c, "TRY", fxRates) || 0), 0),
      total: totalTRY,
    };
  }, [savedLines, fxRates]);

  // Malzeme ihtiyacı yalnız İç Üretim kalanı için (stoktan / Hazır Alım / Fason / ticari hariç)
  const bomNeeds = useMemo(() => {
    const m = {};
    savedLines.forEach((l) => {
      const st = (products.find((x) => x.id === l.product_id)?.supply_type || "").toLowerCase();
      if (st === "traded" || !l.product_id) return;
      const q = l.stock_qty !== undefined
        ? (l.remaining_supply_method === "internal" ? Math.max(0, (Number(l.quantity) || 0) - (Number(l.stock_qty) || 0)) : 0)
        : Number(l.quantity) || 0;
      if (q > 0) m[l.product_id] = (m[l.product_id] || 0) + q;
    });
    return m;
  }, [savedLines, products]);
  const bomKey = JSON.stringify(bomNeeds);
  const bomQty = Object.values(bomNeeds).reduce((s, q) => s + q, 0);

  useEffect(() => {
    if (STEPS[stepIdx]?.key !== "bom" || savedLines.length === 0) return;
    const entries = Object.entries(bomNeeds);
    if (!entries.length) { setBomCheck({ has_bom: false, no_internal: true }); return; }
    setBomLoading(true); setBomCheck(null);
    (async () => {
      try {
        const res = await Promise.all(entries.map(([pid, q]) => api.post(`/products/${pid}/bom/check?quantity=${q}`).then((r) => r.data)));
        const merge = (key, idKey) => {
          const agg = {};
          res.forEach((d) => (d[key] || []).forEach((r) => {
            const k = r[idKey] || r.code || r.name;
            if (!agg[k]) { agg[k] = { ...r }; return; }
            agg[k].required = Math.round((Number(agg[k].required) + Number(r.required)) * 1000) / 1000;
            agg[k].shortage = Math.max(0, Math.round((agg[k].required - Number(agg[k].available)) * 1000) / 1000);
            agg[k].ok = agg[k].shortage <= 0;
          }));
          return Object.values(agg);
        };
        const fabric_rows = merge("fabric_rows", "fabric_id");
        const accessory_rows = merge("accessory_rows", "accessory_id");
        setBomCheck({
          has_bom: res.some((d) => d.has_bom), fabric_rows, accessory_rows,
          all_ok: [...fabric_rows, ...accessory_rows].every((r) => r.ok),
        });
      } catch (err) { toast.error(formatApiError(err)); }
      finally { setBomLoading(false); }
    })();
  }, [stepIdx, bomKey]); // eslint-disable-line

  // ---- Fetch BOM suggested price on product select -------------------------
  const fetchBomSuggestedPrice = useCallback(async (productId) => {
    try {
      const { data } = await api.get(`/products/${productId}/bom/costing?quantity=1`);
      return Number(data?.totals?.suggested_price || 0);
    } catch { return 0; }
  }, []);

  const selectedCustomer = customers.find((c) => c.id === customerId);
  const canGoNext = () => {
    if (stepIdx === 0) return !!customerId;
    if (stepIdx === 1) return savedLines.length > 0;
    return true;
  };

  // ---- Draft helpers -------------------------------------------------------
  const patchDraft = (p) => setDraft((prev) => ({ ...prev, ...p }));
  const resetDraft = () => {
    // FAZ A: yeni draft'a mevcut depo default'u taşı (tek depo varsa)
    const defaultWh = warehouses.length === 1 ? warehouses[0].id : "";
    setDraft({ ...emptyDraft(), warehouse_id: defaultWh });
    setEditingIdx(-1);
  };

  const onProductSelected = async (productId) => {
    // FAZ 5 M5: ürün seçilince currency + KDV oranı ürün kartından okunur
    let prod = productId ? products.find((x) => x.id === productId) : null;
    // Server-side search'te seçilen ürün henüz local `products` state'inde
    // olmayabilir → tek çağrı ile getir ve state'e ekle (downstream find'lar için).
    if (productId && !prod) {
      try {
        const { data } = await api.get(`/products/${productId}`);
        if (data && data.id) {
          prod = data;
          setProducts((prev) => (prev.some((x) => x.id === data.id) ? prev : [data, ...prev]));
        }
      } catch { /* ignore — fallback to defaults below */ }
    }
    const productCurrency = (prod?.sale_currency || "TRY").toUpperCase();
    const productTaxRate = prod?.sale_tax_rate ?? 10;
    // FIX (manuel fiyat regresyonu): yeni ürün seçildi → manuel bayrağı sıfırla,
    // fiyatları temizle. Sonraki async fallback patch'leri bu bayrağa saygı gösterir.
    patchDraft({
      product_id: productId,
      variant_id: "",
      unit_price: 0,
      base_price: 0,
      unit_price_manual: false,
      currency: productCurrency,
      tax_rate: productTaxRate,
    });
    if (!productId) return;

    // FIX: fallback fiyat patch'ini fonksiyonel setDraft ile uygula.
    // - Ürün değiştiyse (kullanıcı bu await sırasında başka ürün seçtiyse) → skip
    // - Kullanıcı manuel fiyat girdiyse (unit_price_manual=true) → skip
    const applyFallbackPrice = (nextUnitPrice, nextBasePrice, nextCurrency) => {
      setDraft((prev) => {
        if (prev.product_id !== productId) return prev;          // ürün değişti
        if (prev.unit_price_manual) return prev;                 // kullanıcı manuel girdi
        const patch = { unit_price: nextUnitPrice, base_price: nextBasePrice };
        if (nextCurrency !== undefined) patch.currency = nextCurrency;
        return { ...prev, ...patch };
      });
    };

    const bomPrice = await fetchBomSuggestedPrice(productId);
    if (bomPrice > 0) {
      // BOM suggested_price backend'te TCMB ile TRY'ye normalize ediliyor.
      // Ürünün sale_currency'si non-TRY ise BOM fiyatını o currency'e çevir.
      let displayPrice = bomPrice;
      let displayCurrency = "TRY";
      if (productCurrency !== "TRY") {
        // TRY tutarını hedef currency'ye böl (1 CUR = X TRY)
        // fxRates burada henüz yüklenmemiş olabilir; guard koy
        // Fallback: eğer fxRates hazır değilse TRY olarak göster
      }
      applyFallbackPrice(displayPrice, displayPrice, displayCurrency);
      // Toast sadece manuel fiyat yoksa gösterilmeli — flag'e bakıp uyar.
      setDraft((prev) => {
        if (prev.product_id === productId && !prev.unit_price_manual) {
          toast.success(`Reçete satış fiyatı çekildi: ₺${fmt2(displayPrice)} (TCMB baz)`);
        }
        return prev;
      });
    } else if (prod?.sale_price) {
      // Ürün kartında sale_price varsa → onu currency ile birlikte getir
      applyFallbackPrice(Number(prod.sale_price), Number(prod.sale_price));
    } else if (prod?.base_price) {
      applyFallbackPrice(Number(prod.base_price), Number(prod.base_price));
    }
  };

  // Currency conversion when currency changes — FAZ 5 M6 (guard'lı, discount_amount dahil)
  const onCurrencyChange = (newCurrency) => {
    const from = (draft.currency || "TRY").toUpperCase();
    const to = (newCurrency || "TRY").toUpperCase();
    if (from === to) return;
    if (!fxRates) {
      toast.error("Güncel döviz kuru alınamadığı için fiyat dönüştürülemedi.");
      return;
    }
    const conv = (val) => convertPrice(val, from, to, fxRates);
    const rateOk = fxRates[from] > 0 && fxRates[to] > 0;
    if (!rateOk) {
      toast.error(`Güncel ${to} kuru bulunamadı, dönüşüm iptal edildi.`);
      return;
    }
    const round2 = (n) => Math.round(Number(n || 0) * 100) / 100;
    patchDraft({
      currency: to,
      unit_price: round2(conv(draft.unit_price)),
      base_price: round2(conv(draft.base_price)),
      discount_amount: round2(conv(draft.discount_amount)),
    });
    toast.success(`TCMB kuru ile dönüştürüldü · 1 ${from} ≈ ${(fxRates[from] / fxRates[to]).toFixed(4)} ${to}`);
  };

  const orderMode = initialMode === "order";
  const lineDecision = (vid) => ((orderMode || draft.fulfill_mode_user) ? {
    stock_qty: Number((draft.stock_alloc || {})[vid || ""] || 0),
    remaining_supply_method: draft.remaining_supply_method || "",
  } : {});
  const commitDraft = () => {
    if (!draft.product_id) { toast.error("Ürün seçilmedi"); return; }
    if (orderMode && !draft.warehouse_id) { toast.error("Depo seçimi zorunlu"); return; }
    if (orderMode && draft.stock_alloc_error) { toast.error("Stoktan miktarı kullanılabilir stoğu aşıyor"); return; }
    // If the user filled in size_quantities boxes, split into one line per
    // filled size — otherwise commit a single line with the manual `quantity`.
    const sq = draft.size_quantities || {};
    const sizeEntries = Object.entries(sq).filter(([, q]) => Number(q) > 0);
    if (sizeEntries.length > 0 && editingIdx < 0) {
      const newLines = sizeEntries.map(([vid, qty]) => ({
        ...draft,
        variant_id: vid,
        quantity: Number(qty),
        ...lineDecision(vid),
        size_quantities: undefined,
        _lid: Math.random().toString(36).slice(2),
      }));
      setSavedLines((prev) => [...prev, ...newLines]);
      toast.success(`${newLines.length} beden kalemi eklendi`);
      resetDraft();
      return;
    }
    if (!Number(draft.quantity) || Number(draft.quantity) <= 0) { toast.error("Adet 0 olamaz"); return; }
    const newLine = { ...draft, ...lineDecision(draft.variant_id), size_quantities: undefined, _lid: Math.random().toString(36).slice(2) };
    if (editingIdx >= 0) {
      setSavedLines((prev) => prev.map((l, i) => (i === editingIdx ? newLine : l)));
      toast.success(`Kalem #${editingIdx + 1} güncellendi`);
    } else {
      setSavedLines((prev) => [...prev, newLine]);
      toast.success(`Kalem #${savedLines.length + 1} eklendi`);
    }
    resetDraft();
  };

  /**
   * Bedenlere göre toplu ekleme:
   * draft.size_quantities = { [variantId]: qty } içindeki her non-zero için
   * ayrı bir satır oluşturur.
   */
  const commitAllSizes = () => {
    if (!draft.product_id) { toast.error("Ürün seçilmedi"); return; }
    if (orderMode && !draft.warehouse_id) { toast.error("Depo seçimi zorunlu"); return; }
    if (orderMode && draft.stock_alloc_error) { toast.error("Stoktan miktarı kullanılabilir stoğu aşıyor"); return; }
    const sq = draft.size_quantities || {};
    const entries = Object.entries(sq).filter(([, q]) => Number(q) > 0);
    if (entries.length === 0) {
      toast.error("En az bir bedene adet giriniz");
      return;
    }
    const newLines = entries.map(([vid, qty]) => ({
      ...draft,
      variant_id: vid,
      quantity: Number(qty),
      ...lineDecision(vid),
      size_quantities: undefined,
      _lid: Math.random().toString(36).slice(2),
    }));
    setSavedLines((prev) => [...prev, ...newLines]);
    toast.success(`${newLines.length} kalem eklendi`);
    resetDraft();
  };

  const editSaved = (idx) => {
    setDraft({ ...savedLines[idx] });
    setEditingIdx(idx);
  };
  const removeSaved = (idx) => {
    if (!window.confirm(`Kalem #${idx + 1} silinsin mi?`)) return;
    setSavedLines((prev) => prev.filter((_, i) => i !== idx));
    if (editingIdx === idx) resetDraft();
  };

  const runAiReview = async () => {
    setAiLoading(true); setAiReview(null);
    try {
      const first = savedLines[0];
      const { data } = await api.post("/copilot/order-review", {
        customer_id: customerId,
        product_id: first.product_id,
        variant_id: first.variant_id || "",
        size_breakdown: {},
        due_date: dueDate || null,
        priority,
      });
      setAiReview(data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setAiLoading(false); }
  };

  const _doSubmit = async (mode = createAs) => {
    setSaving(true);
    setCreateAs(mode);
    try {
      const logo_placements = [];
      const seenLogo = new Set();
      const line_items = savedLines.map((l) => {
        (l.logos || []).forEach((g) => {
          logo_placements.push({
            artwork_id: g.artwork_id, label: g.label || "",
            position: g.position || "", print_method: g.technique || "",
            notes: g.notes || "",
          });
        });
        const lineApps = (l.apps || []).filter((a) => appQty(a, l) > 0);
        lineApps.forEach((a) => (a.logos || []).forEach((g) => {
          const key = `${a.group_id}:${g.artwork_id}`;
          if (seenLogo.has(key)) return;
          seenLogo.add(key);
          logo_placements.push({
            artwork_id: g.artwork_id, label: g.label || "",
            position: a.position || "", print_method: a.type, notes: a.notes || "",
          });
        }));
        const applications = lineApps.map((a) => ({
          group_id: a.group_id, service_code: a.type,
          service_name: APP_TYPES.find(([t]) => t === a.type)?.[1] || a.type,
          quantity: appQty(a, l), unit: "adet", unit_price: Number(a.unit_price) || 0,
          currency: l.currency || "TRY", tax_rate: Number(l.tax_rate) || 0,
          send_to_production: true, production_department_id: a.type,
          apply_all_variants: a.apply_all !== false, variant_ids: a.variant_ids || [],
          artworks: (a.logos || []).map((g) => ({ artwork_id: g.artwork_id, label: g.label || "" })),
          position: a.position || "", dimensions: a.dimensions || "", notes: a.notes || "",
        }));
        const prod = products.find((x) => x.id === l.product_id);
        return {
          product_id: l.product_id,
          product_name: prod?.name || "",
          variant_id: l.variant_id || "",
          quantity: Number(l.quantity) || 0,
          unit_price: Number(l.unit_price) || 0,
          currency: l.currency || "TRY",
          tax_rate: Number(l.tax_rate) || 0,
          discount_percent: Number(l.discount_percent) || 0,
          discount_amount: Number(l.discount_amount) || 0,
          warehouse_id: l.warehouse_id || "",
          size_breakdown: {},
          notes: l.notes || "",
          applications,
          ...(l.stock_qty !== undefined
            ? { stock_qty: Number(l.stock_qty) || 0, remaining_supply_method: l.remaining_supply_method || "" } : {}),
        };
      });
      const { data } = await api.post("/orders/wizard", {
        customer_id: customerId, line_items, logo_placements,
        due_date: dueDate || null, priority, notes, ai_review: aiReview,
        delivery_term: deliveryTerm.trim(), payment_terms: paymentTerms.trim(),
        create_as: mode,
      });
      const label = mode === "quote" ? "Teklif" : "Sipariş";
      toast.success(`${label} oluşturuldu: ${data.order_no}`);
      // P3: Numune → Sipariş bağlantısı — sipariş başarıyla oluştu, numuneyi converted işaretle
      if (sampleConvertIntent?.sample_id) {
        try {
          const lines = savedLines
            .filter((l) => l.from_sample_item_id)
            .map((l) => ({ item_id: l.from_sample_item_id, quantity: Number(l.quantity) || 0 }))
            .filter((l) => l.quantity > 0);
          if (lines.length > 0) {
            await api.post(`/samples/${sampleConvertIntent.sample_id}/mark-converted`, {
              lines, order_id: data.id || data.order_no || "",
            });
            toast.success(`Numune "${sampleConvertIntent.customer_name || ''}" ${lines.reduce((s, l) => s + l.quantity, 0)} adet satışa dönüştürüldü.`);
          }
          sessionStorage.removeItem("operix.sample_convert");
        } catch (err) {
          // Numune callback hata verse bile sipariş oluşturuldu → uyarı ver, akışı bloklamama
          toast.warning(`Sipariş kaydedildi ancak numune converted işaretleme başarısız: ${formatApiError(err)}. /numuneler ekranından manuel işaretleyebilirsiniz.`);
        }
      }
      navigate(mode === "quote" ? "/teklifler" : "/uretim");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); setRiskDialogOpen(false); }
  };

  const submitOrder = async (mode = "order") => {
    setCreateAs(mode);
    // Risk uyarısı — müşteri risk limitini aşacak mı? (yalnızca sipariş için)
    if (!customerId || savedLines.length === 0) return;
    if (mode === "order") {
      setRiskChecking(true);
      try {
        const { data } = await api.get(
          `/finance/risk-check/${customerId}?new_order_amount=${grandTotals.total || 0}`,
        );
        if (data?.will_exceed_risk_limit || ["kirmizi", "koyu_kirmizi"].includes(data?.severity)) {
          setRiskInfo(data);
          setRiskDialogOpen(true);
          return;
        }
      } catch { /* risk kontrolü başarısız — engelleme, devam et */ }
      finally { setRiskChecking(false); }
    }
    await _doSubmit(mode);
  };

  return (
    <div>
      <PageHeader
        title={initialMode === "quote" ? "Teklif Sihirbazı" : "Sipariş Sihirbazı"}
        subtitle={initialMode === "quote"
          ? (lockedMode === "quote" ? "Müşterinize sunacağınız teklifi oluşturun" : "Müşterinize sunacağınız teklifi oluşturun (son adımda sipariş olarak da kaydedebilirsiniz)")
          : "Yeni siparişi profesyonel satır bazlı editörle oluşturun"}
        breadcrumbs={["Ana Sayfa", initialMode === "quote" ? "Teklif Sihirbazı" : "Sipariş Sihirbazı"]}
      />

      <div className="p-6 lg:p-8 max-w-6xl mx-auto">
        <Stepper stepIdx={stepIdx} />

        <Card className="rounded-sm bg-card border-border p-6" data-testid={`wizard-body-${STEPS[stepIdx].key}`}>
          {stepIdx === 0 && (
            <StepCustomer
              customers={customers}
              customerId={customerId}
              setCustomerId={setCustomerId}
              onCustomerCreated={(c) => {
                setCustomers((prev) => [c, ...prev]);
                setCustomerId(c.id);
              }}
            />
          )}
          {stepIdx === 1 && (
            <StepLines
              products={products}
              warehouses={warehouses}
              variantsByProduct={variantsByProduct}
              artwork={artworkByCustomer[customerId] || []}
              previews={artworkPreviews}
              customerId={customerId}
              draft={draft}
              patchDraft={patchDraft}
              onProductSelected={onProductSelected}
              onCurrencyChange={onCurrencyChange}
              onCommit={commitDraft}
              orderMode={initialMode === "order"}
              onCommitAllSizes={commitAllSizes}
              onReset={resetDraft}
              editingIdx={editingIdx}
              savedLines={savedLines}
              onEditSaved={editSaved}
              onRemoveSaved={removeSaved}
              totals={grandTotals}
              totalQty={totalQty}
              fxRates={fxRates}
              onArtworkReload={reloadArtwork}
            />
          )}
          {stepIdx === 2 && (
            <StepBom bomCheck={bomCheck} loading={bomLoading} totalQty={bomQty} />
          )}
          {stepIdx === 3 && (
            <StepReview
              customer={selectedCustomer}
              lines={savedLines}
              products={products}
              variantsByProduct={variantsByProduct}
              warehouses={warehouses}
              totalQty={totalQty}
              totals={grandTotals}
              dueDate={dueDate} setDueDate={setDueDate}
              deliveryTerm={deliveryTerm} setDeliveryTerm={setDeliveryTerm}
              paymentTerms={paymentTerms} setPaymentTerms={setPaymentTerms}
              priority={priority} setPriority={setPriority}
              notes={notes} setNotes={setNotes}
              aiReview={aiReview} aiLoading={aiLoading} runAiReview={runAiReview}
              bomCheck={bomCheck}
            />
          )}
        </Card>

        <div className="flex items-center justify-between mt-6">
          <Button
            data-testid="wizard-back-btn"
            variant="outline"
            onClick={() => setStepIdx((i) => Math.max(0, i - 1))}
            disabled={stepIdx === 0}
            className="rounded-sm"
          >
            <ChevronLeft className="w-4 h-4 mr-1" /> Geri
          </Button>

          {stepIdx < STEPS.length - 1 ? (
            <Button
              data-testid="wizard-next-btn"
              onClick={() => setStepIdx((i) => Math.min(STEPS.length - 1, i + 1))}
              disabled={!canGoNext()}
              className="rounded-sm"
            >
              İleri <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <>
              {initialMode === "quote" && (
              <Button
                data-testid="wizard-submit-quote-btn"
                onClick={() => submitOrder("quote")}
                disabled={saving || riskChecking || totalQty <= 0 || !canCreateAs("quote")}
                title={!canCreateAs("quote") ? "Teklif oluşturma yetkiniz yok" : undefined}
                variant="outline"
                className="rounded-sm border-sky-800 bg-sky-950/30 text-sky-300 hover:bg-sky-950/60"
              >
                {(saving && createAs === "quote") ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileText className="w-4 h-4 mr-1" />}
                Teklif Oluştur
              </Button>
              )}
              {lockedMode !== "quote" && (
              <Button
                data-testid="wizard-submit-btn"
                onClick={() => submitOrder("order")}
                disabled={saving || riskChecking || totalQty <= 0 || !canCreateAs("order")}
                title={!canCreateAs("order") ? "Sipariş oluşturma yetkiniz yok" : undefined}
                className="rounded-sm bg-emerald-600 hover:bg-emerald-500 text-white"
              >
                {(saving && createAs === "order") || riskChecking ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ClipboardCheck className="w-4 h-4 mr-1" />}
                {riskChecking ? "Risk kontrolü…" : "Sipariş Oluştur"}
              </Button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Risk Warning Dialog (P2) */}
      <RiskWarningDialog
        open={riskDialogOpen}
        onOpenChange={setRiskDialogOpen}
        info={riskInfo}
        newOrderAmount={grandTotals.total}
        loading={saving}
        onConfirm={() => _doSubmit("order")}
      />
    </div>
  );
}

// Mobil/tablet: toast veya açılır liste içindeki dokunuş dışarı tıklama sayılmasın
const keepOpenOnInner = (e) => {
  const t = e.detail?.originalEvent?.target || e.target;
  if (t?.closest?.("[data-sonner-toaster],[data-radix-popper-content-wrapper],[data-radix-select-viewport]")) e.preventDefault();
};

// ---- Risk Warning Dialog ---------------------------------------------------
function RiskWarningDialog({ open, onOpenChange, info, newOrderAmount, loading, onConfirm }) {
  if (!info) return null;
  const sevMeta = {
    yesil: { label: "Düşük Risk", color: "text-emerald-400 bg-emerald-950/40 border-emerald-800/60" },
    turuncu: { label: "Orta Risk", color: "text-amber-400 bg-amber-950/40 border-amber-800/60" },
    kirmizi: { label: "Yüksek Risk", color: "text-rose-400 bg-rose-950/40 border-rose-800/60" },
    koyu_kirmizi: { label: "Kritik Risk", color: "text-rose-300 bg-rose-950/60 border-rose-700 font-bold" },
  }[info.severity] || { label: "Risk", color: "text-slate-300 bg-slate-900 border-slate-800" };

  const fmt = (n) => (Number(n) || 0).toLocaleString("tr-TR", { style: "currency", currency: "TRY", minimumFractionDigits: 2 });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent onPointerDownOutside={keepOpenOnInner} onInteractOutside={keepOpenOnInner} className="rounded-sm max-w-lg" data-testid="risk-warning-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-rose-300">
            <AlertTriangle className="w-5 h-5" />
            Müşteri Risk Uyarısı
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            <b className="text-slate-100">{info.customer_name}</b> için bu sipariş,
            müşterinin risk durumunu etkileyecek. Devam etmeden önce lütfen kontrol edin.
          </DialogDescription>
        </DialogHeader>

        <div className={`p-3 rounded-sm border text-xs mb-3 ${sevMeta.color}`} data-testid="risk-severity">
          <div className="text-[10px] uppercase font-mono tracking-widest mb-1">Risk Seviyesi</div>
          <div className="text-sm font-bold">{sevMeta.label}</div>
          {info.block_reason && <div className="mt-1 text-xs">{info.block_reason}</div>}
        </div>

        <div className="space-y-2 text-xs mb-3">
          <RiskRow label="Açık Bakiye" value={fmt(info.outstanding)} />
          {info.overdue_amount > 0 && (
            <RiskRow
              label="Vadesi Geçmiş"
              value={fmt(info.overdue_amount)}
              extra={info.oldest_overdue_days > 0 ? `${info.oldest_overdue_days} gün` : ""}
              danger
            />
          )}
          <RiskRow label="Bu Sipariş (TRY)" value={fmt(newOrderAmount)} highlight />
          <RiskRow label="Toplam Risk (Sonrası)" value={fmt(info.exposure_after)} highlight />
          {info.risk_limit > 0 && (
            <RiskRow
              label="Risk Limiti"
              value={fmt(info.risk_limit)}
              danger={info.will_exceed_risk_limit}
              extra={info.will_exceed_risk_limit ? "AŞILIYOR" : ""}
            />
          )}
        </div>

        {info.will_exceed_risk_limit && (
          <div className="p-3 rounded-sm border border-rose-800/60 bg-rose-950/40 text-rose-200 text-xs mb-2" data-testid="risk-block-message">
            ⚠ Bu sipariş müşterinin risk limitini {fmt(info.exposure_after - info.risk_limit)} aşacak.
            Yönetici onayı ile devam etmeniz önerilir.
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            className="rounded-sm"
            onClick={() => onOpenChange(false)}
            disabled={loading}
            data-testid="risk-cancel-btn"
          >
            Vazgeç
          </Button>
          <Button
            className="rounded-sm bg-rose-600 hover:bg-rose-500 text-white"
            onClick={onConfirm}
            disabled={loading}
            data-testid="risk-confirm-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <AlertTriangle className="w-4 h-4 mr-1" />}
            Riski Kabul Et ve Kaydet
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RiskRow({ label, value, extra, danger, highlight }) {
  return (
    <div className="flex items-center justify-between border-b border-slate-900 pb-1.5">
      <span className={danger ? "text-rose-300" : "text-slate-400"}>{label}</span>
      <span className={
        danger ? "text-rose-300 font-bold" :
        highlight ? "text-slate-100 font-bold" :
        "text-slate-200"
      }>
        {value}
        {extra && <span className={`ml-2 text-[10px] uppercase font-mono tracking-widest ${danger ? "text-rose-400" : "text-slate-500"}`}>{extra}</span>}
      </span>
    </div>
  );
}

// ---- Stepper ---------------------------------------------------------------
function Stepper({ stepIdx }) {
  return (
    <div className="flex items-center justify-between mb-8" data-testid="wizard-stepper">
      {STEPS.map((s, i) => {
        const Icon = s.icon;
        const active = i === stepIdx;
        const done = i < stepIdx;
        return (
          <div key={s.key} className="flex-1 flex items-center">
            <div
              data-testid={`wizard-step-${s.key}`}
              className={`flex flex-col items-center gap-1.5 shrink-0 ${
                active ? "text-primary" : done ? "text-emerald-500" : "text-muted-foreground/60"
              }`}
            >
              <div className={`w-9 h-9 rounded-full flex items-center justify-center border-2 ${
                active ? "border-primary bg-primary/10"
                  : done ? "border-emerald-500 bg-emerald-500/10"
                  : "border-border bg-muted"
              }`}>
                {done ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
              </div>
              <div className="text-[10px] uppercase tracking-wider font-mono hidden sm:block">{s.label}</div>
            </div>
            {i < STEPS.length - 1 && (
              <div className={`flex-1 h-px mx-2 ${i < stepIdx ? "bg-emerald-500" : "bg-border"}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ---- Step 1: Customer -------------------------------------------------------
function StepCustomer({ customers, customerId, setCustomerId, onCustomerCreated }) {
  const [newOpen, setNewOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "", contact_person: "", phone: "", email: "", address: "", tax_number: "", tax_office: "", notes: "",
  });

  const openNew = () => {
    setForm({ name: "", contact_person: "", phone: "", email: "", address: "", tax_number: "", tax_office: "", notes: "" });
    setNewOpen(true);
  };

  const saveNew = async () => {
    if (!form.name.trim()) {
      toast.error("Müşteri adı zorunlu");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        contact_person: form.contact_person.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        address: form.address.trim(),
        tax_no: form.tax_number.trim(),
        tax_office: form.tax_office.trim(),
        notes: form.notes.trim(),
      };
      const { data } = await api.post("/customers", payload);
      toast.success(`${data.name} eklendi ve seçildi`);
      onCustomerCreated?.(data);
      setNewOpen(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-lg font-bold">1. Müşteri Seçimi</h2>
        <Button
          data-testid="wizard-new-customer-btn"
          size="sm"
          onClick={openNew}
          className="rounded-sm bg-emerald-600 hover:bg-emerald-500"
        >
          <UserPlus className="w-3.5 h-3.5 mr-1" /> Yeni Müşteri Ekle
        </Button>
      </div>
      <p className="text-sm text-muted-foreground">
        Bu sipariş hangi müşteriye ait olacak? Ad, iletişim kişisi, telefon veya email üzerinden arayabilirsiniz.
        Listede yoksa <b className="text-emerald-500">"Yeni Müşteri Ekle"</b> ile hızlıca oluşturabilirsiniz.
      </p>
      <SearchPicker
        items={customers}
        value={customerId}
        onChange={setCustomerId}
        getLabel={(c) => c.name}
        getSubtitle={(c) => {
          const parts = [c.contact_person, c.phone, c.email].filter(Boolean);
          if (c.tax_no) parts.push(`VKN: ${c.tax_no}`);
          return parts.join(" · ") || "—";
        }}
        filter={(c, q) => matchesAll(q, [c.name, c.contact_person, c.phone, c.email, c.tax_no, c.tax_office])}
        placeholder="Müşteri / VKN ile ara — isim veya vergi no yazın…"
        testId="wizard-customer"
      />
      {!customerId && customers.length > 0 && (
        <div className="text-[11px] text-muted-foreground italic text-center pt-2">
          Toplam {customers.length} kayıtlı müşteri var. Arayarak seçin veya yenisini ekleyin.
        </div>
      )}

      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent onPointerDownOutside={keepOpenOnInner} onInteractOutside={keepOpenOnInner} className="rounded-sm max-w-lg" data-testid="wizard-new-customer-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-4 h-4 text-emerald-500" /> Yeni Müşteri
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              Sipariş oluşturmayı bırakmadan hızlıca müşteri kaydı açın. Detayları sonradan CRM'den düzenleyebilirsiniz.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Firma / Müşteri Adı *</Label>
              <Input
                data-testid="new-customer-name"
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                className="rounded-sm mt-1"
                autoFocus
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">İletişim Kişisi</Label>
                <Input
                  data-testid="new-customer-contact"
                  value={form.contact_person}
                  onChange={(e) => setForm((p) => ({ ...p, contact_person: e.target.value }))}
                  className="rounded-sm mt-1"
                />
              </div>
              <div>
                <Label className="text-xs">Telefon</Label>
                <Input
                  data-testid="new-customer-phone"
                  value={form.phone}
                  onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
                  className="rounded-sm mt-1"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">E-posta</Label>
                <Input
                  data-testid="new-customer-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                  className="rounded-sm mt-1"
                />
              </div>
              <div>
                <Label className="text-xs">Vergi No / TCKN</Label>
                <div className="mt-1">
                  <VknLookupField
                    value={form.tax_number}
                    onChange={(v) => setForm((p) => ({ ...p, tax_number: v }))}
                    onCompanyFound={(c) => setForm((p) => ({ ...p, name: p.name || c.title }))}
                    testId="new-customer-tax"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">Vergi Dairesi</Label>
                <Input
                  data-testid="new-customer-tax-office"
                  value={form.tax_office}
                  onChange={(e) => setForm((p) => ({ ...p, tax_office: e.target.value }))}
                  placeholder="örn: KADIKÖY"
                  className="rounded-sm mt-1"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Adres</Label>
              <Input
                data-testid="new-customer-address"
                value={form.address}
                onChange={(e) => setForm((p) => ({ ...p, address: e.target.value }))}
                className="rounded-sm mt-1"
              />
            </div>
            <div>
              <Label className="text-xs">Not</Label>
              <Textarea
                data-testid="new-customer-notes"
                value={form.notes}
                onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
                rows={2}
                className="rounded-sm mt-1 text-sm"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setNewOpen(false)} className="rounded-sm">İptal</Button>
            <Button
              onClick={saveNew}
              disabled={saving || !form.name.trim()}
              className="rounded-sm bg-emerald-600 hover:bg-emerald-500"
              data-testid="new-customer-save"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
              Kaydet & Seç
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---- Step 2: Lines (NEW: persistent draft + saved list) --------------------
function StepLines({
  products, warehouses, variantsByProduct, artwork, previews, customerId,
  draft, patchDraft, onProductSelected, onCurrencyChange, onCommit, onCommitAllSizes, onReset,
  editingIdx, savedLines, onEditSaved, onRemoveSaved,
  totals, totalQty, fxRates, onArtworkReload, orderMode,
}) {
  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-bold">2. Sipariş Satırları</h2>
        <p className="text-sm text-muted-foreground">
          Ürün seç → detaylarını gir → <span className="text-primary font-semibold">"Kalemi Ekle"</span> ile listeye ekle.
          Bitmiş satırlar aşağıda kompakt olarak numaralanır.
        </p>
      </div>

      {/* Persistent draft editor */}
      <DraftEditor
        draft={draft}
        patchDraft={patchDraft}
        products={products}
        warehouses={warehouses}
        variants={sortVariants(variantsByProduct[draft.product_id] || [])}
        artwork={artwork}
        previews={previews}
        customerId={customerId}
        onProductSelected={onProductSelected}
        onCurrencyChange={onCurrencyChange}
        onCommit={onCommit}
        orderMode={orderMode}
        onCommitAllSizes={onCommitAllSizes}
        onReset={onReset}
        isEditing={editingIdx >= 0}
        editingIdx={editingIdx}
        fxRates={fxRates}
        onArtworkReload={onArtworkReload}
      />

      {/* Saved compact lines */}
      {savedLines.length > 0 && (
        <div className="space-y-2" data-testid="wizard-saved-lines">
          <div className="text-xs uppercase tracking-wider font-mono text-muted-foreground pt-2">
            Eklenmiş Kalemler ({savedLines.length})
          </div>
          {savedLines.map((line, idx) => (
            <SavedLineRow
              key={line._lid}
              idx={idx}
              line={line}
              products={products}
              variants={sortVariants(variantsByProduct[line.product_id] || [])}
              warehouses={warehouses}
              onEdit={() => onEditSaved(idx)}
              onRemove={() => onRemoveSaved(idx)}
              isEditing={editingIdx === idx}
            />
          ))}
        </div>
      )}

      {savedLines.length === 0 && (
        <div className="border border-dashed border-border rounded-sm p-6 text-center text-sm text-muted-foreground bg-muted/20" data-testid="wizard-empty-lines">
          Henüz sipariş kalemi eklenmedi. Yukarıdan ürünü seçip <span className="text-primary font-semibold">"Kalemi Ekle"</span> ile başlayın.
        </div>
      )}

      {/* Grand totals bar — FAZ 5 M6: her para birimi ayrı grupta gösterilir */}
      <div className="sticky bottom-0 -mx-6 px-6 pt-4 pb-4 border-t border-border bg-card z-10" data-testid="wizard-totals-bar">
        <div className="flex items-center justify-between mb-2">
          <TotalStat label="Toplam Adet" value={totalQty} testId="wizard-total-qty" />
          {totals.currencies.length > 1 && (
            <span className="text-[10px] font-mono text-amber-500 uppercase tracking-wider" data-testid="wizard-mixed-currency-badge">
              Karışık Para Birimi · {totals.currencies.join(" + ")}
            </span>
          )}
        </div>
        {totals.currencies.length === 0 ? (
          <div className="text-xs text-muted-foreground text-center py-4">Kalem ekleyin</div>
        ) : (
          <div className="space-y-2">
            {totals.currencies.map((cur) => {
              const b = totals.perCurrency[cur];
              const sym = currencySymbol(cur);
              return (
                <div key={cur} className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid={`wizard-total-group-${cur}`}>
                  <TotalStat label={`Ara Toplam (${cur})`} value={`${sym} ${fmt2(b.subtotal)}`} testId={`wizard-subtotal-${cur}`} />
                  <TotalStat label={`İskonto (${cur})`} value={`- ${sym} ${fmt2(b.discount)}`} color="text-rose-500" testId={`wizard-discount-${cur}`} />
                  <TotalStat label={`KDV (${cur})`} value={`${sym} ${fmt2(b.tax)}`} testId={`wizard-tax-${cur}`} />
                  <TotalStat label={`Genel Toplam (${cur})`} value={`${sym} ${fmt2(b.total)}`} highlight testId={`wizard-total-${cur}`} />
                </div>
              );
            })}
          </div>
        )}
        {fxRates && (
          <div className="text-[10px] font-mono text-muted-foreground mt-2 text-right" data-testid="wizard-fx-info">
            TCMB {fxRates.date} · USD ₺{fmt2(fxRates.USD)} · EUR ₺{fmt2(fxRates.EUR)}
            {totals.currencies.length > 1 && (
              <span className="ml-2 text-muted-foreground/60">
                (Bilgi amaçlı — teklif toplamı çevrilmez)
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function TotalStat({ label, value, highlight, color, testId }) {
  return (
    <div className={`bg-muted/40 border border-border rounded-sm p-3 ${highlight ? "border-primary/60 bg-primary/5" : ""}`}>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono">{label}</div>
      <div
        data-testid={testId}
        className={`text-lg lg:text-xl font-bold font-mono mt-1 ${
          highlight ? "text-primary" : color || "text-foreground"
        }`}
      >
        {value}
      </div>
    </div>
  );
}

// ---- Draft Editor (persistent form) ---------------------------------------
function DraftEditor({
  draft, patchDraft, products, warehouses, variants, artwork, previews, customerId,
  onProductSelected, onCurrencyChange, onCommit, onCommitAllSizes, onReset,
  isEditing, editingIdx, fxRates, onArtworkReload, orderMode,
}) {
  const { can } = useAuth();
  const canOverDiscount = can?.("orders", "discount_over_limit") || false;
  const [discLimit, setDiscLimit] = useState(10);
  useEffect(() => { api.get("/settings/discount-limit").then(({ data }) => setDiscLimit(Number(data?.approval_limit_percent ?? 10))).catch(() => {}); }, []);
  const c = computeLine(draft);
  const sym = currencySymbol(draft.currency);
  const [phOpen, setPhOpen] = useState(false); // Önceki Fiyatlar dialogu
  // Satış İskonto Yetki Limiti — backend ile aynı (%10)
  const _gross = Number(draft.quantity || 0) * Number(draft.unit_price || 0);
  const _effDisc = Number(draft.discount_percent || 0) + (_gross > 0 ? (Number(draft.discount_amount || 0) / _gross) * 100 : 0);
  const _discExceeds = _effDisc > discLimit + 1e-9;

  const bomPriceInfo =
    draft.base_price > 0 && Number(draft.unit_price) !== Number(draft.base_price)
      ? `Reçete: ${sym}${fmt2(draft.base_price)}`
      : draft.base_price > 0
      ? `Reçete fiyatı ✓`
      : null;

  // TRY equivalent shown when currency is not TRY
  const tryEquiv = draft.currency !== "TRY" && fxRates
    ? convertPrice(c.total, draft.currency, "TRY", fxRates)
    : null;

  return (
    <div
      className={`border rounded-sm p-4 space-y-4 ${
        isEditing ? "border-amber-500/60 bg-amber-500/5" : "border-primary/40 bg-primary/5"
      }`}
      data-testid="wizard-draft-editor"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {isEditing ? (
            <>
              <Edit3 className="w-4 h-4 text-amber-500" />
              <div className="text-xs uppercase font-mono text-amber-600 dark:text-amber-400">
                Kalem #{editingIdx + 1} Düzenleniyor
              </div>
            </>
          ) : (
            <>
              <Plus className="w-4 h-4 text-primary" />
              <div className="text-xs uppercase font-mono text-primary">
                Yeni Kalem
              </div>
            </>
          )}
        </div>
        <Button
          data-testid="wizard-draft-reset"
          variant="ghost" size="sm"
          onClick={onReset}
          className="rounded-sm text-xs h-7"
        >
          <RefreshCcw className="w-3 h-3 mr-1" /> Sıfırla
        </Button>
      </div>

      {/* Row 1: Product | Variant | Warehouse */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <Label className="text-[10px] uppercase text-muted-foreground font-mono flex items-center gap-1">
            <Package className="w-3 h-3" /> Ürün *
          </Label>
          <div className="mt-1">
            <SearchPicker
              items={products}
              value={draft.product_id}
              onChange={onProductSelected}
              getLabel={(p) => p.name}
              getSubtitle={(p) => `${p.category || "—"} · ${p.variant_count || 0} varyant`}
              placeholder="Ürün ara — en az 2 harf…"
              testId="draft-product"
              onSearch={async (query) => {
                // Server-side arama — Ürünler sayfasıyla aynı /api/products?search=
                // endpoint'ini kullanır. Böylece BH entegrasyonundan gelen (limit=500
                // dışında kalan) ürünler de "fig", "itfaiyeci" gibi aramalarda görünür.
                try {
                  const { data } = await api.get("/products", { params: { search: query, limit: 200 } });
                  return Array.isArray(data) ? data : [];
                } catch { return []; }
              }}
            />
          </div>
        </div>
        <div className="md:col-span-2">
          <Label className="text-[10px] uppercase text-muted-foreground font-mono">
            Bedenler {variants.length === 0 && draft.product_id ? "(bu üründe yok)" : ""}
          </Label>
          {!draft.product_id ? (
            <div className="mt-1 h-9 px-3 flex items-center text-xs text-slate-500 border border-slate-800 rounded-sm bg-slate-900/40">
              Önce ürün seçin
            </div>
          ) : variants.length === 0 ? (
            <div className="mt-1 h-9 px-3 flex items-center text-xs text-slate-500 border border-slate-800 rounded-sm bg-slate-900/40">
              Bu ürünün varyantı yok
            </div>
          ) : (
            <div className="mt-1 space-y-2" data-testid="draft-variant-grid">
              {/* Toplu adet girişi — kutucuklara adet yazılan bedenler otomatik satır olur */}
              <div>
                <div className="text-[10px] uppercase font-mono tracking-widest text-slate-500 mb-1.5">
                  Bedenlere Göre Adet — Sadece adet girilen bedenler siparişe eklenir
                </div>
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-1.5" data-testid="draft-size-qty-grid">
                  {variants.map((v) => {
                    const qty = (draft.size_quantities || {})[v.id] || "";
                    return (
                      <div key={v.id} className="flex flex-col">
                        <div className="text-[9px] font-mono text-slate-500 uppercase truncate mb-0.5" title={formatVariantLabel(v.attributes, v.sku || "—")}>
                          {formatVariantLabel(v.attributes, v.sku || "—")}
                        </div>
                        <NumericInput
                          data-testid={`size-qty-${v.id}`}
                          decimal={false}
                          min={0}
                          value={qty}
                          onChange={(val) => {
                            patchDraft({
                              size_quantities: {
                                ...(draft.size_quantities || {}),
                                [v.id]: val === "" || val == null ? "" : Number(val),
                              },
                            });
                          }}
                          placeholder="0"
                          className="rounded-sm h-8 text-center font-mono text-xs"
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
          {/* Yolda stok görünürlüğü — ürün seçilince (varyantlı/varyantsız) */}
          {draft.product_id ? (
            <div className="mt-3">
              <WizardStockInfo
                productId={draft.product_id}
                warehouseId={draft.warehouse_id}
                sizeQuantities={draft.size_quantities || {}}
                totalQuantity={Number(draft.quantity || 0)}
                variants={variants}
                orderMode={orderMode}
              />
            </div>
          ) : null}
        </div>
        <div>
          <Label className="text-[10px] uppercase text-muted-foreground font-mono flex items-center gap-1">
            <Warehouse className="w-3 h-3" /> Depo
          </Label>
          <Select
            value={draft.warehouse_id || "none"}
            onValueChange={(v) => patchDraft({ warehouse_id: v === "none" ? "" : v })}
          >
            <SelectTrigger data-testid="draft-warehouse" className={`rounded-sm mt-1 ${orderMode && !draft.warehouse_id ? "border-rose-500" : ""}`}>
              <SelectValue placeholder={warehouses.length === 0 ? "Depo yok" : "—"} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">— seçilmedi —</SelectItem>
              {warehouses.map((w) => (
                <SelectItem key={w.id} value={w.id}>{w.code} · {w.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {warehouses.length === 0 && (
            <button
              type="button"
              data-testid="draft-warehouse-empty-link"
              onClick={() => window.open("/depolar", "_blank")}
              className="text-[10px] font-mono text-blue-400 hover:text-blue-300 underline underline-offset-2 mt-1"
            >
              Depo yönetimi → Yeni depo ekle
            </button>
          )}
        </div>
      </div>

      {/* Row 2: Qty | Unit Price | Currency | Tax | Discount % | Discount Amt */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 items-start">
        <div>
          <Label className="text-[10px] uppercase text-muted-foreground font-mono">Adet (toplam) *</Label>
          {(() => {
            const sq = draft.size_quantities || {};
            const sumSizes = Object.values(sq).reduce((s, v) => s + (Number(v) || 0), 0);
            const usingGrid = sumSizes > 0;
            return (
              <NumericInput
                data-testid="draft-qty"
                decimal={false}
                min={0}
                readOnly={usingGrid}
                value={usingGrid ? sumSizes : (draft.quantity || "")}
                onChange={(val) => patchDraft({ quantity: Number(val) || 0 })}
                className={`rounded-sm mt-1 font-mono h-9 ${usingGrid ? "bg-slate-900/60 text-blue-300 font-semibold" : ""}`}
                placeholder="0"
                title={usingGrid ? "Beden kutucuklarındaki adetlerin toplamı" : "Manuel adet"}
              />
            );
          })()}
        </div>
        <div>
          <Label className="text-[10px] uppercase text-muted-foreground font-mono flex items-center gap-1">
            <DollarSign className="w-3 h-3" /> Birim Fiyat ({sym})
          </Label>
          <NumericInput
            data-testid="draft-price"
            min={0}
            step="0.01"
            value={draft.unit_price}
            onChange={(val) => patchDraft({ unit_price: Number(val) || 0, unit_price_manual: true })}
            className="rounded-sm mt-1 font-mono h-9"
            placeholder="0,00"
          />
          <PriceHistoryLink productId={draft.product_id} onClick={() => setPhOpen(true)} testId="draft-price-history" />
        </div>
        <div>
          <Label className="text-[10px] uppercase text-muted-foreground font-mono">Para Br.</Label>
          <div className="mt-1">
            <CurrencyChip
              testId="draft-currency"
              value={draft.currency}
              onChange={onCurrencyChange}
              size="md"
            />
          </div>
        </div>
        <div>
          <Label className="text-[10px] uppercase text-muted-foreground font-mono flex items-center gap-1">
            <Percent className="w-3 h-3" /> KDV %
          </Label>
          <NumericInput
            data-testid="draft-tax"
            min={0}
            step="0.5"
            value={draft.tax_rate}
            onChange={(val) => patchDraft({ tax_rate: Number(val) || 0 })}
            className="rounded-sm mt-1 font-mono h-9"
          />
        </div>
        <div>
          <Label className="text-[10px] uppercase text-muted-foreground font-mono flex items-center gap-1">
            <Tag className="w-3 h-3" /> İsk. %
          </Label>
          <NumericInput
            data-testid="draft-disc-percent"
            min={0}
            step="0.5"
            value={draft.discount_percent}
            onChange={(val) => patchDraft({ discount_percent: Number(val) || 0 })}
            className="rounded-sm mt-1 font-mono h-9"
            placeholder="0"
          />
        </div>
        <div>
          <Label className="text-[10px] uppercase text-muted-foreground font-mono">İsk. Tutar ({sym})</Label>
          <NumericInput
            data-testid="draft-disc-amt"
            min={0}
            step="0.01"
            value={draft.discount_amount}
            onChange={(val) => patchDraft({ discount_amount: Number(val) || 0 })}
            className="rounded-sm mt-1 font-mono h-9"
            placeholder="0,00"
          />
        </div>
      </div>
      {(
        <FulfillmentDecision draft={draft} patchDraft={patchDraft} variants={variants} quoteMode={!orderMode}
          supplyType={(products.find((x) => x.id === draft.product_id)?.supply_type || "").toLowerCase()} />
      )}
      {_discExceeds && (
        <div data-testid="draft-disc-authority-warn"
             className={`rounded-sm border px-3 py-1.5 text-[11px] flex items-center gap-2 ${canOverDiscount ? "border-amber-500/40 bg-amber-500/10 text-amber-500" : "border-rose-500/40 bg-rose-500/10 text-rose-500"}`}>
          <Tag className="w-3 h-3 shrink-0" />
          {canOverDiscount
            ? `Dikkat: Efektif iskonto %${_effDisc.toFixed(1)} (limit %${discLimit}). Yönetici yetkinizle kaydedilebilir.`
            : `Efektif iskonto %${_effDisc.toFixed(1)} — %${discLimit} üstü iskonto yönetici onayı ister; teklif "onay bekliyor" olarak kaydedilir.`}
        </div>
      )}
      {bomPriceInfo && (
        <div className="text-[10px] text-primary/80 font-mono -mt-1">{bomPriceInfo}</div>
      )}
      {/* FAZ 5 M5: TCMB kur bilgisi non-TRY için */}
      {draft.currency !== "TRY" && fxRates && fxRates[draft.currency] && (
        <div data-testid="draft-tcmb-info" className="text-[10px] font-mono text-emerald-400/80 -mt-1">
          TCMB Döviz Satış · 1 {draft.currency} = ₺{fmt2(fxRates[draft.currency])}
        </div>
      )}

      {/* Live totals for draft */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
        <div className="bg-background border border-border rounded-sm p-2">
          <div className="text-[9px] uppercase font-mono text-muted-foreground">Brüt</div>
          <div className="font-mono">{sym}{fmt2(c.gross)}</div>
        </div>
        <div className="bg-background border border-border rounded-sm p-2">
          <div className="text-[9px] uppercase font-mono text-muted-foreground">İskonto</div>
          <div className="font-mono text-rose-500">- {sym}{fmt2(c.discount)}</div>
        </div>
        <div className="bg-background border border-border rounded-sm p-2">
          <div className="text-[9px] uppercase font-mono text-muted-foreground">KDV</div>
          <div className="font-mono">{sym}{fmt2(c.tax)}</div>
        </div>
        <div className="bg-primary/10 border border-primary/40 rounded-sm p-2">
          <div className="text-[9px] uppercase font-mono text-primary/80">Kalem Toplamı</div>
          <div data-testid="draft-total" className="font-mono font-bold text-primary">
            {sym}{fmt2(c.total)}
          </div>
          {tryEquiv !== null && (
            <div className="text-[9px] font-mono text-muted-foreground">≈ ₺{fmt2(tryEquiv)}</div>
          )}
        </div>
      </div>

      {/* Baskı / Nakış uygulamaları — her biri ayrı application */}
      <ApplicationsPanel
        draft={draft}
        variants={variants}
        sym={sym}
        logoProps={{ artwork, previews, customerId, onArtworkReload }}
        onPatch={(apps) => patchDraft({ apps })}
      />

      {/* Notes */}
      <div>
        <Label className="text-[10px] uppercase text-muted-foreground font-mono">Kalem Notu</Label>
        <Textarea
          data-testid="draft-notes"
          rows={2}
          value={draft.notes || ""}
          onChange={(e) => patchDraft({ notes: e.target.value })}
          className="rounded-sm mt-1 text-xs"
          placeholder="Özel paketleme, etiket dili, teslim adresi vs."
        />
      </div>

      {/* Önceki Fiyatlar — yalnız birim fiyat + para birimi taslağa aktarılır */}
      <PriceHistoryDialog
        open={phOpen}
        onOpenChange={setPhOpen}
        productId={draft.product_id}
        productName={draft.product_name}
        customerId={customerId}
        onPick={(price, cur) => patchDraft({ unit_price: price, currency: cur, unit_price_manual: true })}
      />

      {/* Commit buttons */}
      <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
        <Button
          data-testid="wizard-draft-commit"
          onClick={onCommit}
          disabled={
            !draft.product_id
            || (orderMode && (!draft.warehouse_id || draft.stock_alloc_error))
            || (
              !Number(draft.quantity)
              && !Object.values(draft.size_quantities || {}).some((q) => Number(q) > 0)
            )
          }
          className="rounded-sm bg-primary hover:bg-primary/90"
        >
          {isEditing ? (
            <><Check className="w-4 h-4 mr-1" /> Değişiklikleri Kaydet</>
          ) : (() => {
            const filled = Object.values(draft.size_quantities || {}).filter((q) => Number(q) > 0).length;
            return (
              <><Plus className="w-4 h-4 mr-1" /> {filled > 0 ? `${filled} Bedeni Ekle` : "Kalemi Ekle"}</>
            );
          })()}
        </Button>
      </div>
    </div>
  );
}

// ---- Saved Line Row (compact) ----------------------------------------------
function SavedLineRow({ idx, line, products, variants, warehouses, onEdit, onRemove, isEditing }) {
  const p = products.find((x) => x.id === line.product_id);
  const v = variants.find((x) => x.id === line.variant_id);
  const w = warehouses.find((x) => x.id === line.warehouse_id);
  const c = computeLine(line);
  const sym = currencySymbol(line.currency);

  // Renk · Beden (yeşil) — program genelinde tutarlı gösterim
  const attrLabel = formatVariantLabel(v?.attributes, v?.sku || "");

  return (
    <div
      className={`grid grid-cols-[32px_1fr_100px_80px_120px_140px_80px] gap-2 items-center border rounded-sm px-3 py-2 text-sm ${
        isEditing ? "border-amber-500/60 bg-amber-500/5" : "border-border bg-muted/30 hover:bg-muted/50"
      }`}
      data-testid={`saved-line-${idx}`}
    >
      <div className="text-xs font-mono font-bold text-primary text-center">
        #{idx + 1}
      </div>
      <div className="min-w-0">
        <div className="font-semibold truncate">{p?.name || "—"}</div>
        <div className="text-[10px] text-muted-foreground font-mono truncate">
          {[w?.code, line.notes].filter(Boolean).join(" · ") || "—"}
          {(line.logos || []).length > 0 && (
            <span className="ml-1.5 text-primary">
              · <Palette className="w-2.5 h-2.5 inline" /> {(line.logos || []).length}
            </span>
          )}
          {(line.apps || []).filter((a) => appQty(a, line) > 0).map((a) => (
            <span key={a.type} className="ml-1.5 text-primary" data-testid={`saved-line-${idx}-app-${a.type}`}>
              · {APP_TYPES.find(([t]) => t === a.type)?.[1]} {appQty(a, line)}
            </span>
          ))}
        </div>
      </div>
      <div className="text-xs text-center">
        <div className="text-[9px] uppercase font-mono text-muted-foreground">Renk · Beden</div>
        <div className="text-emerald-500 dark:text-emerald-400 font-semibold text-xs">{attrLabel || "—"}</div>
      </div>
      <div className="text-center">
        <div className="text-[9px] uppercase font-mono text-muted-foreground">Adet</div>
        <div className="font-mono font-bold">{c.qty}</div>
      </div>
      <div className="text-right">
        <div className="text-[9px] uppercase font-mono text-muted-foreground">Birim Fiyat</div>
        <div className="font-mono">
          {sym}{fmt2(line.unit_price)}
          {(line.discount_percent > 0 || line.discount_amount > 0) && (
            <span className="ml-1 text-rose-500 text-[9px]">
              -{line.discount_percent || 0}%
            </span>
          )}
        </div>
      </div>
      <div className="text-right">
        <div className="text-[9px] uppercase font-mono text-muted-foreground">Toplam</div>
        <div className="font-mono font-bold text-primary">
          {sym}{fmt2(c.total)}
        </div>
      </div>
      <div className="flex items-center justify-end gap-1">
        <button
          data-testid={`saved-line-${idx}-edit`}
          onClick={onEdit}
          className="text-muted-foreground hover:text-amber-500 p-1.5"
          title="Düzenle"
        >
          <Edit3 className="w-3.5 h-3.5" />
        </button>
        <button
          data-testid={`saved-line-${idx}-remove`}
          onClick={onRemove}
          className="text-muted-foreground hover:text-rose-500 p-1.5"
          title="Sil"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

// ---- Logo panel (with manual file upload) ----------------------------------
function LogoPanel({ logos, artwork, previews, customerId, onArtworkReload, onPatch, simple = false, title = "Logo & Baskı", tp = "draft-logo" }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const addFromLibrary = (a) => {
    onPatch([
      ...logos,
      {
        artwork_id: a.id,
        label: a.label || a.original_filename || "Logo",
        position: "gogus_sol",
        technique: "baski",
        notes: "",
      },
    ]);
    setPickerOpen(false);
    toast.success("Logo eklendi");
  };

  const patchLogo = (i, p) => onPatch(logos.map((x, j) => (i === j ? { ...x, ...p } : x)));
  const removeLogo = (i) => onPatch(logos.filter((_, j) => j !== i));

  // Manual upload — opens file picker, uploads to artwork lib, adds to logos
  const openManualUpload = () => {
    if (!customerId) { toast.error("Önce müşteri seçin"); return; }
    fileInputRef.current?.click();
  };
  const handleFiles = async (files) => {
    if (!files || files.length === 0 || !customerId) return;
    setUploading(true);
    try {
      const newLogos = [];
      for (const file of Array.from(files)) {
        const fd = new FormData();
        fd.append("customer_id", customerId);
        fd.append("label", file.name.replace(/\.[^.]+$/, ""));
        fd.append("position", "");
        fd.append("file", file);
        try {
          const { data } = await api.post("/artwork/upload", fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          newLogos.push({
            artwork_id: data.id,
            label: data.label || data.original_filename,
            position: "gogus_sol",
            technique: "baski",
            notes: "",
          });
        } catch (e) {
          toast.error(`${file.name}: ${formatApiError(e)}`);
        }
      }
      if (newLogos.length > 0) {
        onPatch([...logos, ...newLogos]);
        toast.success(`${newLogos.length} logo yüklendi ve eklendi`);
        onArtworkReload?.();
      }
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <div className="border border-border rounded-sm bg-background p-3 space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase font-mono text-muted-foreground flex items-center gap-1.5">
          <Palette className="w-3.5 h-3.5" /> {title}
          <span className="text-primary font-bold">({logos.length})</span>
        </div>
        <div className="flex items-center gap-1.5">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml,application/pdf"
            className="hidden"
            data-testid={`${tp}-file-input`}
            onChange={(e) => handleFiles(e.target.files)}
          />
          <Button
            data-testid={`${tp}-upload`}
            variant="outline" size="sm"
            className="rounded-sm text-xs h-7"
            onClick={openManualUpload}
            disabled={uploading || !customerId}
            title={!customerId ? "Önce müşteri seçin" : "Bilgisayardan yükle"}
          >
            {uploading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Upload className="w-3 h-3 mr-1" />}
            Bilgisayardan Yükle
          </Button>
          <Button
            data-testid={`${tp}-from-lib`}
            variant="outline" size="sm"
            className="rounded-sm text-xs h-7"
            onClick={() => {
              if (!customerId) { toast.error("Önce müşteri seçin"); return; }
              setPickerOpen((v) => !v);
            }}
            disabled={!customerId}
            title={!customerId ? "Önce müşteri seçin" : "Bu müşterinin logo bankasından seç"}
          >
            <ImageIcon className="w-3 h-3 mr-1" /> Logo Bankasından Seç ({artwork.length})
          </Button>
        </div>
      </div>

      {pickerOpen && artwork.length === 0 && (
        <div
          data-testid={`${tp}-artwork-empty`}
          className="p-4 border border-dashed border-border rounded-sm bg-muted/30 text-center"
        >
          <ImageIcon className="w-6 h-6 text-muted-foreground mx-auto mb-2" />
          <div className="text-xs text-muted-foreground">
            Bu müşterinin Logo Bankasında kayıtlı logo bulunmuyor.
          </div>
          <div className="text-[10px] text-muted-foreground/70 mt-1">
            "Bilgisayardan Yükle" ile logo ekleyebilirsiniz; yüklenen logolar müşterinin bankasına da kaydedilir.
          </div>
        </div>
      )}

      {pickerOpen && artwork.length > 0 && (
        <div className="grid grid-cols-3 md:grid-cols-6 gap-2 p-2 border border-dashed border-border rounded-sm bg-muted/30">
          {artwork.map((a) => (
            <button
              key={a.id}
              data-testid={`${tp}-artwork-${a.id}`}
              onClick={() => addFromLibrary(a)}
              className="text-left border border-border bg-card rounded-sm p-1.5 hover:border-primary transition-colors"
            >
              <div className="aspect-square bg-muted rounded-sm flex items-center justify-center overflow-hidden mb-1">
                {previews[a.id] ? (
                  <img src={previews[a.id]} alt="" className="max-w-full max-h-full object-contain" />
                ) : <ImageIcon className="w-4 h-4 text-muted-foreground" />}
              </div>
              <div className="text-[10px] font-semibold truncate">{a.label || a.original_filename}</div>
            </button>
          ))}
        </div>
      )}

      {logos.length === 0 ? (
        <div className="text-xs text-muted-foreground italic text-center py-3">
          Bu kalem için logo/baskı yok. "Bilgisayardan Yükle" veya "Logo Bankasından Seç" ile ekleyin.
        </div>
      ) : (
        <div className="space-y-2">
          {logos.map((g, li) => {
            const preview = previews[g.artwork_id];
            return (
              <div
                key={li}
                data-testid={`${tp}-${li}`}
                className={`grid ${simple ? "grid-cols-[48px_1fr_28px]" : "grid-cols-[48px_1fr_1fr_1fr_28px]"} gap-2 items-center bg-card border border-border rounded-sm p-2`}
              >
                <div className="w-12 h-12 bg-muted rounded-sm flex items-center justify-center overflow-hidden">
                  {preview ? (
                    <img src={preview} alt="" className="max-w-full max-h-full object-contain" />
                  ) : <ImageIcon className="w-4 h-4 text-muted-foreground" />}
                </div>
                <Input
                  value={g.label || ""}
                  onChange={(e) => patchLogo(li, { label: e.target.value })}
                  className="rounded-sm h-8 text-xs"
                  placeholder="Etiket"
                />
                {!simple && <Select value={g.position || "gogus_sol"} onValueChange={(v) => patchLogo(li, { position: v })}>
                  <SelectTrigger data-testid={`draft-logo-${li}-position`} className="rounded-sm h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {POSITION_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>}
                {!simple && <Select value={g.technique || "baski"} onValueChange={(v) => patchLogo(li, { technique: v })}>
                  <SelectTrigger data-testid={`draft-logo-${li}-technique`} className="rounded-sm h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TECHNIQUE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>}
                <button
                  data-testid={`${tp}-${li}-remove`}
                  onClick={() => removeLogo(li)}
                  className="text-muted-foreground hover:text-rose-500 p-1"
                  title="Sil"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---- Baskı / Nakış uygulamaları (draft.apps → kalem applications[]) -----------
function ApplicationsPanel({ draft, variants, sym, logoProps, onPatch }) {
  const apps = draft.apps || [];
  const sizeRows = Object.entries(draft.size_quantities || {}).filter(([, q]) => Number(q) > 0);
  const toggle = (t) => onPatch(apps.some((a) => a.type === t) ? apps.filter((a) => a.type !== t) : [...apps, {
    group_id: Math.random().toString(36).slice(2) + Date.now().toString(36), type: t, apply_all: true,
    variant_ids: [], logos: [], position: "", dimensions: "", notes: "", unit_price: 0,
  }]);
  const patchApp = (t, p) => onPatch(apps.map((a) => (a.type === t ? { ...a, ...p } : a)));
  return (
    <div className="border border-border rounded-sm bg-background p-3 space-y-3" data-testid="draft-applications">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-xs uppercase font-mono text-muted-foreground flex items-center gap-1.5">
          <Palette className="w-3.5 h-3.5" /> Uygulamalar (Baskı / Nakış)
        </div>
        <div className="flex gap-1.5">
          {APP_TYPES.map(([t, l]) => {
            const on = apps.some((a) => a.type === t);
            return (
              <button key={t} type="button" data-testid={`draft-app-toggle-${t}`} onClick={() => toggle(t)}
                className={`px-2.5 py-1 text-[11px] rounded-sm border flex items-center gap-1 transition-colors ${on ? "border-primary bg-primary/15 text-primary" : "border-border text-muted-foreground hover:border-primary/50"}`}>
                {on ? <Check className="w-3 h-3" /> : <Plus className="w-3 h-3" />} {l}
              </button>
            );
          })}
        </div>
      </div>
      {apps.length === 0 && (
        <div className="text-xs text-muted-foreground italic">Baskı ve/veya Nakış seçin. Dosya yüklemek tek başına operasyon oluşturmaz.</div>
      )}
      {apps.map((a) => (
        <ApplicationCard key={a.type} app={a} draft={draft} variants={variants} sizeRows={sizeRows} sym={sym}
          logoProps={logoProps} onPatch={(p) => patchApp(a.type, p)} />
      ))}
    </div>
  );
}

function ApplicationCard({ app, draft, variants, sizeRows, sym, logoProps, onPatch }) {
  const label = APP_TYPES.find(([t]) => t === app.type)?.[1];
  const tp = `draft-app-${app.type}`;
  const vLabel = (vid) => { const v = variants.find((x) => x.id === vid); return v ? formatVariantLabel(v.attributes, v.sku || "—") : "—"; };
  const toggleVariant = (vid) => {
    const ids = app.variant_ids || [];
    onPatch({ variant_ids: ids.includes(vid) ? ids.filter((x) => x !== vid) : [...ids, vid] });
  };
  return (
    <div className="border border-primary/30 rounded-sm p-3 space-y-2 bg-primary/5" data-testid={`${tp}-card`}>
      <div className="flex items-center justify-between text-xs">
        <span className="font-semibold text-primary">{label}</span>
        <span className="font-mono" data-testid={`${tp}-qty`}>{appQty(app, draft)} adet</span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Select value={app.position || "none"} onValueChange={(v) => onPatch({ position: v === "none" ? "" : v })}>
          <SelectTrigger data-testid={`${tp}-position`} className="rounded-sm h-8 text-xs"><SelectValue placeholder="Konum" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">— Konum —</SelectItem>
            {POSITION_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input data-testid={`${tp}-dimensions`} value={app.dimensions || ""} onChange={(e) => onPatch({ dimensions: e.target.value })}
          placeholder="Ölçü (örn. 8x8 cm)" className="rounded-sm h-8 text-xs" />
        <NumericInput data-testid={`${tp}-price`} min={0} step="0.01" value={app.unit_price}
          onChange={(val) => onPatch({ unit_price: Number(val) || 0 })} placeholder={`Adet fiyatı (${sym})`} className="rounded-sm h-8 text-xs font-mono" />
        <Input data-testid={`${tp}-notes`} value={app.notes || ""} onChange={(e) => onPatch({ notes: e.target.value })}
          placeholder="Not" className="rounded-sm h-8 text-xs" />
      </div>
      {sizeRows.length > 0 && (
        <div className="space-y-1.5">
          <label className="flex items-center gap-2 text-[11px] cursor-pointer">
            <input type="checkbox" data-testid={`${tp}-apply-all`} checked={app.apply_all !== false}
              onChange={(e) => onPatch({ apply_all: e.target.checked, variant_ids: e.target.checked ? [] : sizeRows.map(([vid]) => vid) })} />
            Tüm varyantlara uygula
          </label>
          {app.apply_all === false && (
            <div className="flex flex-wrap gap-1.5">
              {sizeRows.map(([vid, q]) => {
                const on = (app.variant_ids || []).includes(vid);
                return (
                  <button key={vid} type="button" data-testid={`${tp}-variant-${vid}`} onClick={() => toggleVariant(vid)}
                    className={`px-2 py-0.5 text-[11px] rounded-sm border font-mono ${on ? "border-emerald-500 bg-emerald-500/10 text-emerald-500" : "border-border text-muted-foreground"}`}>
                    {vLabel(vid)} · {q}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
      <LogoPanel {...logoProps} logos={app.logos || []} onPatch={(logos) => onPatch({ logos })}
        simple title={`${label} Tasarım Dosyası`} tp={tp} />
    </div>
  );
}

// ---- Step 3: BOM -----------------------------------------------------------
function StepBom({ bomCheck, loading, totalQty }) {
  const rows = [
    ...((bomCheck?.fabric_rows || []).map((r) => ({ ...r, kind: "Kumaş", icon: Scissors }))),
    ...((bomCheck?.accessory_rows || []).map((r) => ({ ...r, kind: "Aksesuar", icon: Boxes }))),
  ];
  return (
    <div className="space-y-4">
      <h2 className="text-lg font-bold">3. Malzeme İhtiyaç Listesi (Ürün Reçetesi)</h2>
      <p className="text-sm text-muted-foreground">
        Yalnız İç Üretim yapılacak {totalQty} adet için ürün reçetesine göre malzeme ihtiyacı ve stok durumu.
        Stoktan, Hazır Alım ile karşılanan ve ticari ürün kalemleri dahil değildir.
      </p>

      {loading ? (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="w-5 h-5 text-primary animate-spin" />
        </div>
      ) : bomCheck?.no_internal ? (
        <div className="border border-dashed border-border bg-muted/40 rounded-sm p-6 text-center" data-testid="wizard-bom-no-internal">
          <Layers className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <div className="text-sm font-semibold mb-1">İç Üretim yapılacak kalem yok</div>
          <div className="text-xs text-muted-foreground">Kalemler stoktan / Hazır Alım ile karşılanıyor; reçete malzeme ihtiyacı oluşmaz.</div>
        </div>
      ) : !bomCheck?.has_bom ? (
        <div className="border border-dashed border-border bg-muted/40 rounded-sm p-6 text-center" data-testid="wizard-bom-empty">
          <Layers className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <div className="text-sm font-semibold mb-1">Bu ürün için ürün reçetesi tanımlı değil</div>
          <div className="text-xs text-muted-foreground">Ürünler sayfasından ürün reçetesi tanımlayabilirsiniz.</div>
        </div>
      ) : (
        <>
          <div className={`rounded-sm p-3 border text-sm flex items-center gap-2 ${
            bomCheck.all_ok ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
              : "border-rose-500/50 bg-rose-500/10 text-rose-600 dark:text-rose-400"
          }`} data-testid="wizard-bom-status">
            {bomCheck.all_ok ? <ShieldCheck className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
            <span className="font-semibold">
              {bomCheck.all_ok ? "Tüm malzemeler yeterli" : "Bazı malzemeler eksik — stok tedariki gerekli"}
            </span>
          </div>

          <div className="border border-border rounded-sm overflow-hidden">
            <table className="w-full text-sm" data-testid="wizard-bom-table">
              <thead className="bg-muted/40 border-b border-border">
                <tr className="text-[10px] uppercase font-mono text-muted-foreground">
                  <th className="text-left px-3 py-2">Malzeme</th>
                  <th className="text-right px-3 py-2">Adet Başı</th>
                  <th className="text-right px-3 py-2">Gerekli</th>
                  <th className="text-right px-3 py-2">Mevcut</th>
                  <th className="text-right px-3 py-2">Eksik</th>
                  <th className="text-center px-3 py-2">Durum</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {rows.map((r, i) => {
                  const Icon = r.icon;
                  const per = r.consumption_per_unit ?? r.qty_per_unit;
                  return (
                    <tr key={i} className={r.ok ? "" : "bg-rose-500/5"}>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-2">
                          <Icon className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                          <div>
                            <div className="font-medium">{r.name || "—"}</div>
                            <div className="text-[10px] font-mono text-muted-foreground">{r.code} · {r.kind}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono">{per} {r.unit}</td>
                      <td className="px-3 py-2.5 text-right font-mono text-primary font-bold">{r.required} {r.unit}</td>
                      <td className="px-3 py-2.5 text-right font-mono">{r.available} {r.unit}</td>
                      <td className={`px-3 py-2.5 text-right font-mono ${r.shortage > 0 ? "text-rose-500 font-bold" : "text-muted-foreground"}`}>
                        {r.shortage > 0 ? `-${r.shortage}` : "—"}
                      </td>
                      <td className="px-3 py-2.5 text-center">
                        {r.ok ? (
                          <span className="inline-flex items-center gap-1 text-emerald-500 text-xs font-semibold">
                            <Check className="w-3.5 h-3.5" /> Yeterli
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-rose-500 text-xs font-semibold">
                            <AlertTriangle className="w-3.5 h-3.5" /> Eksik
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// ---- Step 4: Review --------------------------------------------------------
function StepReview({
  customer, lines, products, variantsByProduct, warehouses,
  totalQty, totals, dueDate, setDueDate, priority, setPriority,
  deliveryTerm, setDeliveryTerm, paymentTerms, setPaymentTerms,
  notes, setNotes, aiReview, aiLoading, runAiReview, bomCheck,
}) {
  const totalLogos = lines.reduce((s, l) => s + (l.logos || []).length, 0);
  return (
    <div className="space-y-5">
      <h2 className="text-lg font-bold">4. Özet & AI İnceleme</h2>

      {/* Ürün kalemleri tablosu — kullanıcı isteğiyle EN ÜSTTE. */}
      <div className="border border-border rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 border-b border-border">
            <tr className="text-[10px] uppercase font-mono text-muted-foreground">
              <th className="text-left px-3 py-2">#</th>
              <th className="text-left px-3 py-2">Ürün</th>
              <th className="text-left px-3 py-2">Renk · Beden</th>
              <th className="text-right px-3 py-2">Adet</th>
              <th className="text-right px-3 py-2">Fiyat</th>
              <th className="text-right px-3 py-2">İsk.</th>
              <th className="text-right px-3 py-2">KDV</th>
              <th className="text-right px-3 py-2">Toplam</th>
              <th className="text-center px-3 py-2">Logo</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {lines.map((l, i) => {
              const p = products.find((x) => x.id === l.product_id);
              const v = (variantsByProduct[l.product_id] || []).find((x) => x.id === l.variant_id);
              const c = computeLine(l);
              const sym = currencySymbol(l.currency);
              const attrLabel = v ? formatVariantLabel(v.attributes, v.sku || "—") : "—";
              return (
                <tr key={i} className="text-sm">
                  <td className="px-3 py-2 text-primary font-mono">#{i + 1}</td>
                  <td className="px-3 py-2 font-medium">{p?.name || "—"}</td>
                  <td className="px-3 py-2 text-emerald-500 dark:text-emerald-400 font-semibold text-xs">{attrLabel}</td>
                  <td className="px-3 py-2 text-right font-mono">{c.qty}</td>
                  <td className="px-3 py-2 text-right font-mono">{sym}{fmt2(l.unit_price)}</td>
                  <td className="px-3 py-2 text-right font-mono text-rose-500">
                    {c.discount > 0 ? `${sym}${fmt2(c.discount)}` : "—"}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-muted-foreground">%{l.tax_rate}</td>
                  <td className="px-3 py-2 text-right font-mono text-primary font-bold">
                    {sym}{fmt2(c.total)}
                  </td>
                  <td className="px-3 py-2 text-center text-xs text-muted-foreground">
                    {(l.logos || []).length || "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Tutar özeti — FAZ 5 M6: her para birimi ayrı satırda */}
      <div className="bg-muted/40 border border-border rounded-sm p-4 space-y-2" data-testid="wizard-totals-summary">
        <SummaryRow label="Müşteri" value={customer?.name || "—"} />
        <SummaryRow label="Kalem Sayısı" value={`${lines.length} kalem`} />
        <SummaryRow label="Toplam Adet" value={totalQty} highlight />
        {totals.currencies.length > 1 && (
          <SummaryRow
            label="Para Birimi"
            value={`Karışık · ${totals.currencies.join(" + ")}`}
          />
        )}
        {totals.currencies.map((cur) => {
          const b = totals.perCurrency[cur];
          const sym = currencySymbol(cur);
          const suffix = totals.currencies.length > 1 ? ` (${cur})` : "";
          return (
            <div key={cur} data-testid={`review-total-group-${cur}`} className="space-y-1">
              <SummaryRow label={`Ara Toplam${suffix}`} value={`${sym} ${fmt2(b.subtotal)}`} />
              <SummaryRow label={`İskonto${suffix}`} value={`- ${sym} ${fmt2(b.discount)}`} />
              <SummaryRow label={`KDV${suffix}`} value={`${sym} ${fmt2(b.tax)}`} />
              <SummaryRow label={`Genel Toplam${suffix}`} value={`${sym} ${fmt2(b.total)}`} highlight />
            </div>
          );
        })}
        <SummaryRow label="Logo/Baskı" value={`${totalLogos} adet`} />
        {bomCheck?.has_bom && (
          <SummaryRow
            label="Malzeme Durumu"
            value={bomCheck.all_ok ? "✓ Tüm stoklar yeterli" : "⚠ Eksik var"}
          />
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label className="text-xs uppercase text-muted-foreground font-mono">Termin Tarihi</Label>
          <Input
            data-testid="wizard-due-date"
            type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)}
            className="rounded-sm mt-1"
          />
        </div>
        <div>
          <Label className="text-xs uppercase text-muted-foreground font-mono">Öncelik</Label>
          <Select value={priority} onValueChange={setPriority}>
            <SelectTrigger data-testid="wizard-priority" className="rounded-sm mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="dusuk">Düşük</SelectItem>
              <SelectItem value="normal">Normal</SelectItem>
              <SelectItem value="yuksek">Yüksek</SelectItem>
              <SelectItem value="acil">Acil</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label className="text-xs uppercase text-muted-foreground font-mono">Termin</Label>
          <Input data-testid="wizard-delivery-term" value={deliveryTerm}
            onChange={(e) => setDeliveryTerm(e.target.value)}
            placeholder="Örn: Sipariş onayından itibaren 15 iş günü" className="rounded-sm mt-1" />
        </div>
        <div>
          <Label className="text-xs uppercase text-muted-foreground font-mono">Ödeme Şekli</Label>
          <Input data-testid="wizard-payment-terms" value={paymentTerms}
            onChange={(e) => setPaymentTerms(e.target.value)}
            placeholder="Örn: Peşin · %50 Sipariş / %50 Teslimat · 30 Gün Vadeli" className="rounded-sm mt-1" />
        </div>
      </div>

      <div>
        <Label className="text-xs uppercase text-muted-foreground font-mono">Notlar</Label>
        <Textarea
          data-testid="wizard-notes" rows={2}
          value={notes} onChange={(e) => setNotes(e.target.value)}
          className="rounded-sm mt-1"
        />
      </div>

      <div className="border-t border-border pt-5 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-bold flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-primary" /> AI Sipariş İnceleme
            </div>
            <div className="text-xs text-muted-foreground">Gecikme riski, stok yeterliliği ve öneriler.</div>
          </div>
          <Button
            data-testid="wizard-ai-review-btn"
            onClick={runAiReview} disabled={aiLoading}
            variant="outline"
            className="rounded-sm"
          >
            {aiLoading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RefreshCcw className="w-4 h-4 mr-1.5" />}
            {aiReview ? "Yeniden İncele" : "AI İncele"}
          </Button>
        </div>
        {aiReview && <AiReviewCard review={aiReview} />}
      </div>
    </div>
  );
}

function SummaryRow({ label, value, highlight }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <div className="text-xs uppercase font-mono text-muted-foreground shrink-0">{label}</div>
      <div className={`text-sm text-right truncate ${highlight ? "text-primary font-bold" : ""}`}>
        {value}
      </div>
    </div>
  );
}

function AiReviewCard({ review }) {
  const risk = review.risk_level || "orta";
  const riskMeta = {
    dusuk: { label: "Düşük Risk", cls: "bg-emerald-500/10 border-emerald-500/40 text-emerald-600 dark:text-emerald-400", icon: ShieldCheck },
    orta: { label: "Orta Risk", cls: "bg-amber-500/10 border-amber-500/40 text-amber-600 dark:text-amber-400", icon: AlertTriangle },
    yuksek: { label: "Yüksek Risk", cls: "bg-rose-500/10 border-rose-500/40 text-rose-600 dark:text-rose-400", icon: AlertTriangle },
  }[risk] || { label: risk, cls: "bg-muted border-border", icon: AlertTriangle };
  const RiskIcon = riskMeta.icon;
  return (
    <div className="bg-muted/40 border border-border rounded-sm p-4 space-y-3" data-testid="wizard-ai-review-result">
      <div className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-sm border text-xs font-semibold ${riskMeta.cls}`}>
        <RiskIcon className="w-3.5 h-3.5" /> {riskMeta.label}
      </div>
      <div className="text-sm leading-relaxed">{review.summary}</div>
      <div className="grid grid-cols-2 gap-3">
        <div className={`p-2.5 rounded-sm border text-xs ${review.stock_ok ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" : "border-rose-500/40 bg-rose-500/10 text-rose-600 dark:text-rose-400"}`}>
          <div className="uppercase font-mono text-[10px] opacity-70">Stok</div>
          <div className="font-semibold">{review.stock_ok ? "Yeterli" : "Yetersiz"}</div>
        </div>
        <div className={`p-2.5 rounded-sm border text-xs ${review.timeline_ok ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" : "border-rose-500/40 bg-rose-500/10 text-rose-600 dark:text-rose-400"}`}>
          <div className="uppercase font-mono text-[10px] opacity-70">Zamanlama</div>
          <div className="font-semibold">{review.timeline_ok ? "Uygun" : "Risk Var"}</div>
        </div>
      </div>
      {Array.isArray(review.warnings) && review.warnings.length > 0 && (
        <div>
          <div className="text-[10px] uppercase font-mono text-muted-foreground mb-1">Uyarılar</div>
          <ul className="space-y-1 text-sm text-amber-600 dark:text-amber-400">
            {review.warnings.map((w, i) => <li key={i} className="flex gap-2"><span>•</span><span>{w}</span></li>)}
          </ul>
        </div>
      )}
      {Array.isArray(review.suggestions) && review.suggestions.length > 0 && (
        <div>
          <div className="text-[10px] uppercase font-mono text-muted-foreground mb-1">Öneriler</div>
          <ul className="space-y-1 text-sm">
            {review.suggestions.map((s, i) => <li key={i} className="flex gap-2"><span className="text-primary">→</span><span>{s}</span></li>)}
          </ul>
        </div>
      )}
    </div>
  );
}
